<template>
  <CustomTitleBar />
  <div class="login-container">
    <h2 class="title">注册账户</h2>

    <!-- 手机号输入框 -->
    <div class="input-group">
      <van-field v-model="phoneNumber" placeholder="输入手机号" :border="false">
        <template #label>
          <div class="country-code" @click="showAreaCodePicker = true">
            <SvgIcon name="phone" size="16" />
            <span>+{{ phoneAreaPrefix }}</span>
            <van-icon name="arrow-down" size="14" color="#666666" />
          </div>
        </template>
      </van-field>
    </div>

    <!-- 密码输入框 -->
    <div class="input-group">
      <van-field v-model="password" type="password" placeholder="输入密码" :border="false">
        <template #label>
          <SvgIcon name="pwd" />
        </template>
      </van-field>
    </div>

    <!-- 图形验证码 -->
    <div class="input-group">
      <van-field v-model="captcha" placeholder="输入图形验证码" :border="false">
        <template #label>
          <SvgIcon name="code" size="16" />
        </template>
        <template #right-icon>
          <el-image class="captcha-img" :src="captchaUrl" @click="refreshCaptcha" />
        </template>
      </van-field>
    </div>

    <!-- 验证码输入框 -->
    <div class="input-group">
      <van-field v-model="verificationCode" placeholder="输入验证码" :border="false">
        <template #label>
          <SvgIcon name="code2" size="16" />
        </template>
        <template #right-icon>
          <van-button size="small" plain type="primary" @click="sendVerificationCode" :disabled="cooldownTime > 0">
            {{ cooldownTime > 0 ? `${cooldownTime}s` : '获取验证码' }}
          </van-button>
        </template>
      </van-field>
    </div>

    <!-- 邀请码输入框 - 添加在验证码输入框后面 -->
    <div class="input-group">
      <van-field v-model="inviteCode" placeholder="输入邀请码" :border="false">
        <template #label>
          <SvgIcon name="invite" size="16" />
        </template>
      </van-field>
    </div>

    <!-- 注册按钮 -->
    <van-button type="primary" block round @click="submitForm">注册</van-button>

    <!-- 协议勾选 -->
    <div class="agreement">
      <van-checkbox v-model="agreed" shape="round" icon-size="12">
        注册即表示同意<a href="https://www.jingchat.cn/userProtocol.html" target="_blank">《服务条款》</a>和<a
          href="https://www.hnmengyuqianfang.cn/features.html" target="_blank">《隐私协议》</a>
      </van-checkbox>
    </div>

    <van-popup v-model:show="showAreaCodePicker" position="bottom">
      <van-picker :columns="areaCodeColumns" @confirm="onAreaCodeConfirm" @cancel="showAreaCodePicker = false"
        :title="'选择区号'" show-toolbar />
    </van-popup>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import CustomTitleBar from "@/components/CustomTitleBar/index.vue"
import { getCaptcha, sendSmsCode, registerApi } from '@/api/system'

const router = useRouter()

// 表单数据
const phoneAreaPrefix = ref('86')
const phoneNumber = ref('')
const password = ref('')
const captcha = ref('')
const captchaUrl = ref('')
const verificationCode = ref('')
const agreed = ref(false)
const cooldownTime = ref(0)
const showAreaCodePicker = ref(false)
let timer: ReturnType<typeof setInterval> | null = null
const clientType = ref('web') // 客户端类型
const onlyCode = ref('') // 唯一码
const inviteCode = ref('') // 邀请码

// 区号数据
const areaCodeColumns = [
  { text: '中国 +86', value: '86' },
  { text: '中国香港 +852', value: '852' },
  { text: '中国澳门 +853', value: '853' },
  { text: '中国台湾 +886', value: '886' },
  { text: '日本 +81', value: '81' },
  { text: '韩国 +82', value: '82' },
  { text: '美国 +1', value: '1' },
]

// 表单验证
const validateForm = () => {
  if (!phoneNumber.value) {
    showToast('请输入手机号')
    return false
  }
  if (!/^1[3-9]\d{9}$/.test(phoneNumber.value)) {
    showToast('请输入正确的手机号')
    return false
  }
  if (!password.value) {
    showToast('请输入密码')
    return false
  }
  if (password.value.length < 6) {
    showToast('密码长度不能少于6位')
    return false
  }
  if (!captcha.value) {
    showToast('请输入图形验证码')
    return false
  }
  if (!verificationCode.value) {
    showToast('请输入验证码')
    return false
  }
  if (!inviteCode.value) {
    showToast('请输入邀请码')
    return false
  }
  if (!agreed.value) {
    showToast('请先同意服务条款和隐私协议')
    return false
  }
  return true
}

// 提交注册
const submitForm = async () => {
  if (!validateForm()) return

  try {
    const res = await registerApi({
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value,
      password: password.value,
      captcha: verificationCode.value, // 图形验证码
      clientType: clientType.value,
      inviteCode: inviteCode.value || '' // 邀请码，可选
    })

    if (res.code === 200) {
      showToast('注册成功')
      router.push('/login')
    } else {
      showToast(res.msg)
    }
  } catch (error) {
    console.error('注册失败：', error)
    showToast('注册失败，请重试')
  }
}

// 刷新图形验证码
const refreshCaptcha = async () => {
  try {
    const res = await getCaptcha()
    captchaUrl.value = URL.createObjectURL(res)
  } catch (error) {
    console.error('验证码获取失败：', error)
  }
}

// 发送验证码
const sendVerificationCode = async () => {
  if (!phoneNumber.value || cooldownTime.value > 0) return

  try {
    const res = await sendSmsCode({
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value,
      captcha: captcha.value,
      type: 1, // 注册验证码类型
    })

    if (res.code === 200) {
      showToast('验证码已发送')
      cooldownTime.value = 60
      timer = setInterval(() => {
        cooldownTime.value--
        if (cooldownTime.value <= 0 && timer) {
          clearInterval(timer)
          timer = null
        }
      }, 1000)
    } else {
      showToast(res.msg)
      refreshCaptcha()
    }
  } catch (error) {
    console.error('发送验证码失败：', error)
    showToast('发送失败，请重试')
  }
}

// 区号选择
const onAreaCodeConfirm = (item: any) => {
  phoneAreaPrefix.value = item.selectedValues[0]
  showAreaCodePicker.value = false
}

onMounted(() => {
  refreshCaptcha()
})

onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
  }
  if (captchaUrl.value) {
    URL.revokeObjectURL(captchaUrl.value)
  }
})
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  background-color: #fff;
  padding: 20px 32px;
  box-sizing: border-box;

  .title {
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    margin: 40px 0;
  }

  .input-group {
    background: transparent;
    border-bottom: 1px solid #ebedf0;
    border-radius: 0;
    margin-bottom: 16px;
    overflow: hidden;

    .captcha-img {
      width: 100px;
      height: 32px;
      cursor: pointer;
    }

    :deep(.van-field) {
      background: transparent;

      &::after {
        display: none;
      }
    }

    :deep(.van-field__label) {
      width: auto;
      display: flex;
      align-items: center;
    }
  }

  .country-code {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-right: 8px;
    border-right: 1px solid #ebedf0;
    margin-right: 8px;

    span {
      font-size: 14px;
      color: #333;
      font-weight: normal;
    }
  }

  .action-links {
    display: flex;
    justify-content: space-between;
    margin: 16px 0 24px;

    span {
      color: #666;
      font-size: 14px;
    }
  }

  .agreement {
    text-align: center;
    font-size: 12px;
    color: #999;
    margin: 16px 0;

    :deep(.van-checkbox__label) {
      color: #999;
    }

    a {
      color: $primary-color;
      text-decoration: none;
    }
  }

  .verification-login {
    text-align: center;
    margin-top: 60px;

    .van-icon {
      display: block;
      margin: 0 auto 8px;
    }

    span {
      font-size: 14px;
      color: #666;
    }
  }
}
</style>